import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Volume2, Bell, Mic, MicOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useSipContext } from '@/context/SipContext';
import type { SoftPhoneState } from '../types';

interface SettingsBlockProps {
  localStatePhone: SoftPhoneState;
  handleConnectPhone: (event: React.MouseEvent | null, connectionStatus: boolean) => void;
  handleSettingsSlider: (name: string, newValue: number) => void;
  handleConnectOnStart: (event: React.ChangeEvent<HTMLInputElement>, newValue: boolean) => void;
  handleNotifications: (event: React.ChangeEvent<HTMLInputElement>, newValue: boolean) => void;
  callVolume: number;
}

export function SettingsBlock({
  localStatePhone,
  handleConnectPhone,
  handleSettingsSlider,
  handleConnectOnStart,
  handleNotifications,
}: SettingsBlockProps) {
  const globalSipContext = useSipContext();
  const [micPermissionStatus, setMicPermissionStatus] = useState<'unknown' | 'granted' | 'denied' | 'testing'>('unknown');

  // Always use new SIP implementation
  const isConnected = globalSipContext.status.isConnected && globalSipContext.status.isRegistered;
  const isConnecting = globalSipContext.status.isConnecting;
  const connectionError = globalSipContext.status.error;

  // Test microphone permissions
  const testMicrophonePermissions = async () => {
    setMicPermissionStatus('testing');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      console.log('Microphone test successful');
      setMicPermissionStatus('granted');

      // Stop the test stream
      stream.getTracks().forEach(track => track.stop());

      // Initialize audio
      if (globalSipContext.initializeAudio) {
        await globalSipContext.initializeAudio();
      }
    } catch (error) {
      console.error('Microphone test failed:', error);
      setMicPermissionStatus('denied');
    }
  };
  return (
    <div className="space-y-4 p-4">
      {/* Connection Control */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Connection</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm">Status</span>
            <Badge variant={isConnected ? "default" : isConnecting ? "secondary" : "outline"}>
              {isConnecting ? "Connecting..." : isConnected ? "Connected" : "Disconnected"}
            </Badge>
          </div>

          {/* Show error if any */}
          {connectionError && (
            <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
              {connectionError}
            </div>
          )}

          {/* Connection is managed automatically */}
          <div className="text-xs text-muted-foreground bg-blue-50 p-2 rounded">
            Connection managed automatically. Status: {globalSipContext.status.isConnected ? 'Connected' : 'Disconnected'} |
            Registration: {globalSipContext.status.isRegistered ? 'Registered' : 'Not Registered'}
          </div>
        </CardContent>
      </Card>

      {/* Audio Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Audio Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Volume2 className="h-4 w-4" />
              <Label className="text-sm">Call Volume</Label>
            </div>
            <Slider
              value={[localStatePhone.callVolume * 100]}
              onValueChange={(value) => handleSettingsSlider('callVolume', value[0] / 100)}
              max={100}
              step={1}
              className="w-full"
            />
            <span className="text-xs text-muted-foreground">
              {Math.round(localStatePhone.callVolume * 100)}%
            </span>
          </div>

          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Bell className="h-4 w-4" />
              <Label className="text-sm">Ring Volume</Label>
            </div>
            <Slider
              value={[localStatePhone.ringVolume * 100]}
              onValueChange={(value) => handleSettingsSlider('ringVolume', value[0] / 100)}
              max={100}
              step={1}
              className="w-full"
            />
            <span className="text-xs text-muted-foreground">
              {Math.round(localStatePhone.ringVolume * 100)}%
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Microphone Test */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Microphone Test</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm">Permission Status</span>
            <Badge variant={
              micPermissionStatus === 'granted' ? "default" :
              micPermissionStatus === 'denied' ? "destructive" :
              micPermissionStatus === 'testing' ? "secondary" : "outline"
            }>
              {micPermissionStatus === 'granted' ? 'Granted' :
               micPermissionStatus === 'denied' ? 'Denied' :
               micPermissionStatus === 'testing' ? 'Testing...' : 'Unknown'}
            </Badge>
          </div>

          <Button
            onClick={testMicrophonePermissions}
            disabled={micPermissionStatus === 'testing'}
            className="w-full"
            variant="outline"
          >
            {micPermissionStatus === 'testing' ? (
              <>
                <MicOff className="mr-2 h-4 w-4 animate-pulse" />
                Testing Microphone...
              </>
            ) : (
              <>
                <Mic className="mr-2 h-4 w-4" />
                Test Microphone Access
              </>
            )}
          </Button>

          {micPermissionStatus === 'denied' && (
            <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
              Microphone access denied. Please click the microphone icon in your browser&apos;s address bar and allow access, then try again.
            </div>
          )}

          {micPermissionStatus === 'granted' && (
            <div className="text-xs text-green-600 bg-green-50 p-2 rounded">
              Microphone access granted! You should now be able to make calls.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preferences */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="connect-on-start" className="text-sm">
              Connect on start
            </Label>
            <input
              id="connect-on-start"
              type="checkbox"
              checked={localStatePhone.connectOnStart}
              onChange={(e) => handleConnectOnStart(e, e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="notifications" className="text-sm">
              Notifications
            </Label>
            <input
              id="notifications"
              type="checkbox"
              checked={localStatePhone.notifications}
              onChange={(e) => handleNotifications(e, e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}